import { EnrollmentStatus, Payment } from '@spike/payments-model';

export const CARD_SCHEME_IDS = new Set(['visa', 'mc', 'amex']);
export const GENERIC_CARD_PAYMENT_IDS = new Set(['credit_card', 'card']);

// Card scheme payment method ids from master data (all of them are named "Credit Card" there).
const CARD_BRAND_NAMES: Record<string, string> = {
    visa: 'Visa',
    mc: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    maestro: 'Maestro',
    cup: 'UnionPay'
};

const GENERIC_CARD_PAYMENT_NAME = 'credit card';
const GENERIC_CARD_LABEL = 'Credit/Debit Card';

const getCardLastFour = (digits: string | null | undefined): string | null => {
    const lastFour = (digits ?? '').replace(/\D/g, '').slice(-4);
    return lastFour.length > 0 ? lastFour : null;
};

const getPaymentMethodId = (payment: Payment): string => payment.paymentMethod?.id?.trim().toLowerCase() ?? '';

const getPaymentCardLastFour = (payment: Payment): string | null =>
    getCardLastFour(payment.paymentMethod?.cardDigits ?? payment.card?.digits);

/**
 * Whether the payment was made with a card (any scheme, or a generic card payment).
 */
export const isCardPayment = (payment: Payment): boolean => {
    const id = getPaymentMethodId(payment);

    if (id === 'cash') {
        return false;
    }

    return (
        CARD_BRAND_NAMES[id] !== undefined ||
        GENERIC_CARD_PAYMENT_IDS.has(id) ||
        (payment.paymentMethod?.name ?? '').trim().toLowerCase() === GENERIC_CARD_PAYMENT_NAME ||
        getPaymentCardLastFour(payment) !== null
    );
};

/**
 * Label shown for the payment method of a payment (e.g. in the receipt).
 * Card payments show brand + last 4 digits when available ("Visa •••• 1234").
 * Otherwise, the generic "Credit Card" label is replaced with "Credit/Debit Card",
 * because the card funding source (credit/debit) is not always known.
 */
export const getPaymentMethodLabel = (payment: Payment): string => {
    const name = payment.paymentMethod?.name ?? '';

    if (getPaymentMethodId(payment) === 'cash') {
        return name || 'Cash';
    }

    if (!isCardPayment(payment)) {
        return name;
    }

    const brand = CARD_BRAND_NAMES[getPaymentMethodId(payment)];
    const lastFour = getPaymentCardLastFour(payment);

    if (lastFour) {
        return `${brand ?? 'Card'} •••• ${lastFour}`;
    }

    return brand ?? GENERIC_CARD_LABEL;
};

export enum salesReportItemvalueType {
    Quantity = 'Quantity',
    Money = 'Money'
}

const Formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
});

export const isNegativeSalesReportItemValue = (value: number | undefined) => {
    if (value && value > 0) {
        return false;
    } else {
        return true;
    }
};

export const formatSalesReportItemValue = (value: number | undefined, type: salesReportItemvalueType) => {
    if (!value) {
        if (type === salesReportItemvalueType.Quantity) {
            return '0';
        } else {
            return '$0.00';
        }
    } else {
        return isNegativeSalesReportItemValue(value)
            ? '(' + Formatter.format(value).replaceAll('-', '') + ')'
            : type === salesReportItemvalueType.Quantity
            ? value
            : Formatter.format(value);
    }
};

export const isNewAccount = (status: EnrollmentStatus | undefined): boolean => {
    return status?.verificationStatus !== undefined;
};

export const formatPayoutAmount = (amount: number | string): string => {
    const value = Number(String(amount).replace(/,/g, ''));
    return Formatter.format(Number.isFinite(value) ? value : 0);
};

export const isPayoutNowMarketplaceEnabled = (marketplaceId: number | undefined): boolean => {
    const enabledMarketplaceIds = (process.env.REACT_APP_PAYOUT_NOW_MARKETPLACE_IDS || '')
        .split(',')
        .map(id => id.replaceAll(/\D/g, ''))
        .filter(id => id !== '')
        .map(id => Number(id));

    return (
        marketplaceId !== undefined &&
        (enabledMarketplaceIds.length === 0 || enabledMarketplaceIds.includes(marketplaceId))
    );
};
